import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../lib/lang';
import { forgotPassword } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function ForgotPasswordPage() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t('emailRequired') || 'Veuillez entrer votre adresse email');
      return;
    }
    setLoading(true);
    try {
      await forgotPassword({
        email: email.trim().toLowerCase(),
        lang,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('errorOccurred') || 'Une erreur est survenue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-rolex-green">
      <Header />
      <main className="flex-1 flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-cream/65 hover:text-gold text-sm mb-5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t('backToLogin') || 'Retour à la connexion'}
          </Link>

          <Card className="bg-white border-2 border-[hsl(var(--gold)/0.30)] shadow-2xl">
            <CardContent className="p-7 sm:p-9">
              {sent ? (
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-500 mx-auto mb-5 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <CheckCircle2 className="w-11 h-11 text-white" strokeWidth={2.5} />
                  </div>
                  <h1 className="font-display text-2xl font-extrabold text-rolex-green mb-2">
                    {t('emailSentTitle') || 'Email envoyé !'}
                  </h1>
                  <p className="text-rolex-green/70 text-sm mb-6 leading-relaxed">
                    {t('forgotPasswordSent') || `Si l'adresse `}<strong className="text-rolex-green">{email}</strong>{` est associée à un compte, vous recevrez un email avec un lien pour réinitialiser votre mot de passe.`}
                  </p>
                  <div className="bg-cream rounded-xl p-4 border border-gold/20 mb-5 text-left">
                    <p className="text-xs text-rolex-green/75 leading-relaxed">
                      <strong>💡 {t('checkSpamTitle') || 'Vérifiez vos spams'}</strong>
                      <br />
                      {t('checkSpam') || `L'email peut prendre 1-2 minutes pour arriver. Si vous ne le trouvez pas dans votre boîte de réception, vérifiez vos spams ou indésirables.`}
                    </p>
                  </div>
                  <p className="text-xs text-rolex-green/55 mb-5">
                    {t('linkValidFor') || 'Le lien sera valide pendant 1 heure.'}
                  </p>
                  <Link to="/login">
                    <Button className="btn-gold w-full h-12 font-bold">
                      {t('backToLogin') || 'Retour à la connexion'}
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-gold-gradient mx-auto mb-4 flex items-center justify-center shadow-lg shadow-gold/40">
                      <Lock className="w-8 h-8 text-rolex-green" strokeWidth={2.2} />
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-rolex-green mb-2">
                      {t('forgotPasswordTitle') || 'Mot de passe oublié ?'}
                    </h1>
                    <p className="text-rolex-green/70 text-sm leading-relaxed">
                      {t('forgotPasswordSubtitle') || 'Entrez votre email — nous vous enverrons un lien sécurisé pour créer un nouveau mot de passe.'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-rolex-green font-semibold">
                        {t('email') || 'Email'}
                      </Label>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rolex-green/45" />
                        <Input
                          id="email"
                          type="email"
                          autoComplete="email"
                          autoFocus
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="votre@email.com"
                          className="pl-9 h-12 border-gold/30 focus:border-gold"
                          required
                          data-testid="forgot-password-email"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !email.trim()}
                      className="btn-gold w-full h-12 font-bold gap-2"
                      data-testid="forgot-password-submit"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                      {loading
                        ? (t('sending') || 'Envoi…')
                        : (t('sendResetLink') || 'Envoyer le lien de réinitialisation')}
                    </Button>
                  </form>

                  <div className="mt-6 pt-5 border-t border-gold/15 text-center">
                    <p className="text-xs text-rolex-green/55">
                      {t('rememberPassword') || 'Vous vous souvenez ?'}{' '}
                      <Link to="/login" className="text-gold-dark font-semibold hover:underline">
                        {t('login') || 'Se connecter'}
                      </Link>
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
